// =============================================================
// HTML templates for the site sections.
// All copy comes from the i18n dictionaries; this module is
// pure HTML composition.
// =============================================================

import { t, tList, getLocale } from './i18n';

const esc = (s: string) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// ---------- HERO ----------
export const heroTpl = (): string => `
  <section class="hero" aria-labelledby="heroTitle">
    <div class="hero__left reveal">
      <p class="hero__eyebrow">
        <span class="hero__eyebrow-bar"></span>
        <span data-i18n="hero.eyebrow">${esc(t('hero.eyebrow'))}</span>
      </p>
      <h1 class="hero__title" id="heroTitle">
        <span class="hero__line" data-i18n="hero.titleLine1">${esc(t('hero.titleLine1'))}</span>
        <span class="hero__line hero__line--accent" data-i18n="hero.titleLine2">${esc(t('hero.titleLine2'))}</span>
      </h1>
      <ul class="hero__services" role="list">
        <li class="hero__service" data-target="strength">
          <h3 data-i18n="services.strength.title">${esc(t('services.strength.title'))}</h3>
          <p class="hero__service-desc" data-i18n="services.strength.short">${esc(t('services.strength.short'))}</p>
        </li>
        <li class="hero__service" data-target="conditioning">
          <h3 data-i18n="services.conditioning.title">${esc(t('services.conditioning.title'))}</h3>
          <p class="hero__service-desc" data-i18n="services.conditioning.short">${esc(t('services.conditioning.short'))}</p>
        </li>
        <li class="hero__service" data-target="recovery">
          <h3 data-i18n="services.recovery.title">${esc(t('services.recovery.title'))}</h3>
          <p class="hero__service-desc" data-i18n="services.recovery.short">${esc(t('services.recovery.short'))}</p>
        </li>
      </ul>
      <a class="btn btn--primary" href="/contact">
        <span data-i18n="hero.cta">${esc(t('hero.cta'))}</span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter" /></svg>
      </a>
    </div>
    <aside class="hero__right reveal" aria-label="${esc(t('contactCard.email'))}">
      <picture class="hero__logo-wrap">
        <img src="/logo.svg"       alt="333 Studio Budapest" class="hero__logo hero__logo--strength" width="220" height="220" loading="eager" decoding="async" />
        <img src="/logo-yoga.svg"  alt="333 Yoga Budapest"    class="hero__logo hero__logo--yoga"     width="220" height="220" loading="eager" decoding="async" />
      </picture>
      <div class="hero__contact">
        <div class="hero__contact-row">
          <span class="hero__contact-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm0 2v2h10V4H7zm0 4v10h10V8H7z" fill="currentColor"/></svg>
          </span>
          <div>
            <h3 data-i18n="contactCard.phone">${esc(t('contactCard.phone'))}</h3>
            <p><a href="tel:+36208243351">+36 20 824 3351</a></p>
          </div>
        </div>
        <div class="hero__contact-row">
          <span class="hero__contact-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z" fill="currentColor"/></svg>
          </span>
          <div>
            <h3 data-i18n="contactCard.email">${esc(t('contactCard.email'))}</h3>
            <p><a href="mailto:info@333studio.hu">info@333studio.hu</a></p>
          </div>
        </div>
        <div class="hero__contact-row">
          <span class="hero__contact-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M12 2a8 8 0 0 1 8 8c0 5.4-7 11.5-7.4 11.8a1 1 0 0 1-1.2 0C11 21.5 4 15.4 4 10a8 8 0 0 1 8-8zm0 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" fill="currentColor"/></svg>
          </span>
          <div>
            <h3 data-i18n="contactCard.locations">${esc(t('contactCard.locations'))}</h3>
            <p>
              <a href="https://maps.app.goo.gl/scZeLUY8tijP7oVH7" target="_blank" rel="noopener">A22 — 1013 Attila út 22.</a><br />
              <a href="https://maps.app.goo.gl/mALis5cuz3t4xzCH6" target="_blank" rel="noopener">M15 — 1016 Mihály utca 15</a>
            </p>
          </div>
        </div>
      </div>
    </aside>
  </section>
`;

// ---------- ABOUT ----------
export const aboutTpl = (): string => `
  <section class="about" id="about" aria-labelledby="aboutTitle">
    <div class="about__inner">
      <header class="section-head reveal">
        <p class="section-eyebrow" data-i18n="about.eyebrow">${esc(t('about.eyebrow'))}</p>
        <h2 class="section-title" id="aboutTitle">
          <span data-i18n="about.title1">${esc(t('about.title1'))}</span><br />
          <span data-i18n="about.title2">${esc(t('about.title2'))}</span> <span class="accent-mark" data-i18n="about.titleAccent">${esc(t('about.titleAccent'))}</span>
        </h2>
      </header>
      <div class="about__grid">
        <p class="about__lede reveal" data-i18n="about.lede">${esc(t('about.lede'))}</p>
        <dl class="about__stats reveal" aria-label="${esc(t('about.stats.founded'))}">
          <div class="stat">
            <dt data-i18n="about.stats.founded">${esc(t('about.stats.founded'))}</dt>
            <dd><span data-counter="2019">0</span></dd>
          </div>
          <div class="stat">
            <dt data-i18n="about.stats.locations">${esc(t('about.stats.locations'))}</dt>
            <dd><span data-counter="2">0</span></dd>
          </div>
          <div class="stat">
            <dt data-i18n="about.stats.trainers">${esc(t('about.stats.trainers'))}</dt>
            <dd><span data-counter="6">0</span></dd>
          </div>
          <div class="stat">
            <dt data-i18n="about.stats.members">${esc(t('about.stats.members'))}</dt>
            <dd><span data-counter="240">0</span>+</dd>
          </div>
        </dl>
      </div>
    </div>
  </section>
`;

// ---------- DISCIPLINES ----------
export const disciplinesTpl = (): string => {
  const chip = (key: string) => tList(key).map(i => `<li>${esc(i)}</li>`).join('');
  return `
  <section class="disciplines" id="disciplines" aria-labelledby="discTitle">
    <div class="disciplines__inner">
      <header class="section-head reveal">
        <p class="section-eyebrow" data-i18n="disciplines.eyebrow">${esc(t('disciplines.eyebrow'))}</p>
        <h2 class="section-title" id="discTitle">
          <span data-i18n="disciplines.title1">${esc(t('disciplines.title1'))}</span><br />
          <span data-i18n="disciplines.title2">${esc(t('disciplines.title2'))}</span> <span class="accent-mark" data-i18n="disciplines.titleAccent">${esc(t('disciplines.titleAccent'))}</span>
        </h2>
      </header>
      <div class="disc reveal">
        <div class="disc__tabs" role="tablist">
          <button class="disc__tab is-active" role="tab" aria-selected="true" aria-controls="panel-strength" id="tab-strength" data-tab="strength" type="button">
            <span class="disc__tab-num">01</span>
            <span class="disc__tab-label" data-i18n="disciplines.tabs.strength">${esc(t('disciplines.tabs.strength'))}</span>
          </button>
          <button class="disc__tab" role="tab" aria-selected="false" aria-controls="panel-conditioning" id="tab-conditioning" data-tab="conditioning" type="button">
            <span class="disc__tab-num">02</span>
            <span class="disc__tab-label" data-i18n="disciplines.tabs.conditioning">${esc(t('disciplines.tabs.conditioning'))}</span>
          </button>
          <button class="disc__tab" role="tab" aria-selected="false" aria-controls="panel-recovery" id="tab-recovery" data-tab="recovery" type="button">
            <span class="disc__tab-num">03</span>
            <span class="disc__tab-label" data-i18n="disciplines.tabs.recovery">${esc(t('disciplines.tabs.recovery'))}</span>
          </button>
        </div>
        <div class="disc__panels">
          <article class="disc__panel is-active" role="tabpanel" id="panel-strength" aria-labelledby="tab-strength" data-panel="strength">
            <h3 data-i18n="disciplines.strength.title">${esc(t('disciplines.strength.title'))}</h3>
            <p data-i18n="disciplines.strength.body">${esc(t('disciplines.strength.body'))}</p>
            <ul class="disc__chips" role="list">${chip('disciplines.strength.chips')}</ul>
          </article>
          <article class="disc__panel" role="tabpanel" id="panel-conditioning" aria-labelledby="tab-conditioning" data-panel="conditioning" hidden>
            <h3 data-i18n="disciplines.conditioning.title">${esc(t('disciplines.conditioning.title'))}</h3>
            <p data-i18n="disciplines.conditioning.body">${esc(t('disciplines.conditioning.body'))}</p>
            <ul class="disc__chips" role="list">${chip('disciplines.conditioning.chips')}</ul>
          </article>
          <article class="disc__panel" role="tabpanel" id="panel-recovery" aria-labelledby="tab-recovery" data-panel="recovery" hidden>
            <h3 data-i18n="disciplines.recovery.title">${esc(t('disciplines.recovery.title'))}</h3>
            <p data-i18n="disciplines.recovery.body">${esc(t('disciplines.recovery.body'))}</p>
            <ul class="disc__chips" role="list">${chip('disciplines.recovery.chips')}</ul>
          </article>
        </div>
      </div>
    </div>
  </section>
`;
};

// ---------- LOCATIONS ----------
export const locationsTpl = (): string => `
  <section class="locations" id="locations" aria-labelledby="locTitle">
    <div class="locations__inner">
      <header class="section-head reveal">
        <p class="section-eyebrow" data-i18n="locations.eyebrow">${esc(t('locations.eyebrow'))}</p>
        <h2 class="section-title" id="locTitle">
          <span data-i18n="locations.title1">${esc(t('locations.title1'))}</span><br />
          <span class="accent-mark" data-i18n="locations.titleAccent">${esc(t('locations.titleAccent'))}</span>
        </h2>
      </header>
      <div class="locations__grid">
        <article class="loc reveal" data-loc="a22">
          <p class="loc__code">A22</p>
          <h3 class="loc__name" data-i18n="locations.a22.name">${esc(t('locations.a22.name'))}</h3>
          <p class="loc__address" data-i18n="locations.a22.address">${esc(t('locations.a22.address'))}</p>
          <ul class="loc__meta">
            <li><span data-i18n="locations.meta.floor">${esc(t('locations.meta.floor'))}</span><b data-i18n="locations.a22.floor">${esc(t('locations.a22.floor'))}</b></li>
            <li><span data-i18n="locations.meta.size">${esc(t('locations.meta.size'))}</span><b data-i18n="locations.a22.size">${esc(t('locations.a22.size'))}</b></li>
            <li><span data-i18n="locations.meta.gear">${esc(t('locations.meta.gear'))}</span><b data-i18n="locations.a22.gear">${esc(t('locations.a22.gear'))}</b></li>
          </ul>
          <a class="loc__cta" href="https://maps.app.goo.gl/scZeLUY8tijP7oVH7" target="_blank" rel="noopener">
            <span data-i18n="locations.a22.cta">${esc(t('locations.a22.cta'))}</span>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M9 7h8v8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" /></svg>
          </a>
        </article>
        <article class="loc reveal" data-loc="m15">
          <p class="loc__code">M15</p>
          <h3 class="loc__name" data-i18n="locations.m15.name">${esc(t('locations.m15.name'))}</h3>
          <p class="loc__address" data-i18n="locations.m15.address">${esc(t('locations.m15.address'))}</p>
          <ul class="loc__meta">
            <li><span data-i18n="locations.meta.floor">${esc(t('locations.meta.floor'))}</span><b data-i18n="locations.m15.floor">${esc(t('locations.m15.floor'))}</b></li>
            <li><span data-i18n="locations.meta.size">${esc(t('locations.meta.size'))}</span><b data-i18n="locations.m15.size">${esc(t('locations.m15.size'))}</b></li>
            <li><span data-i18n="locations.meta.gear">${esc(t('locations.meta.gear'))}</span><b data-i18n="locations.m15.gear">${esc(t('locations.m15.gear'))}</b></li>
          </ul>
          <a class="loc__cta" href="https://maps.app.goo.gl/mALis5cuz3t4xzCH6" target="_blank" rel="noopener">
            <span data-i18n="locations.m15.cta">${esc(t('locations.m15.cta'))}</span>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17 17 7M9 7h8v8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" /></svg>
          </a>
        </article>
      </div>
    </div>
  </section>
`;

// ---------- CONTACT ----------
export const contactTpl = (): string => `
  <section class="contact" id="contact" aria-labelledby="contactTitle">
    <div class="contact__inner">
      <header class="section-head reveal">
        <p class="section-eyebrow" data-i18n="contact.eyebrow">${esc(t('contact.eyebrow'))}</p>
        <h2 class="section-title" id="contactTitle">
          <span data-i18n="contact.title1">${esc(t('contact.title1'))}</span> <span class="accent-mark" data-i18n="contact.titleAccent">${esc(t('contact.titleAccent'))}</span>
        </h2>
        <p class="section-sub" data-i18n="contact.subtitle">${esc(t('contact.subtitle'))}</p>
      </header>
      <form class="contact__form reveal" id="contactForm" novalidate>
        <div class="field">
          <label for="name" data-i18n="contact.name">${esc(t('contact.name'))}</label>
          <input id="name" name="name" type="text" autocomplete="name" required minlength="2" />
        </div>
        <div class="field">
          <label for="email" data-i18n="contact.email">${esc(t('contact.email'))}</label>
          <input id="email" name="email" type="email" autocomplete="email" required />
        </div>
        <div class="field">
          <label for="phone" data-i18n="contact.phone">${esc(t('contact.phone'))} <span class="field__opt" data-i18n="contact.phoneOpt">(${esc(t('contact.phoneOpt'))})</span></label>
          <input id="phone" name="phone" type="tel" autocomplete="tel" />
        </div>
        <div class="field">
          <label for="location" data-i18n="contact.location">${esc(t('contact.location'))}</label>
          <select id="location" name="location" required>
            <option value="" data-i18n="contact.locationChoose">${esc(t('contact.locationChoose'))}</option>
            <option value="A22" data-i18n="contact.locationA22">${esc(t('contact.locationA22'))}</option>
            <option value="M15" data-i18n="contact.locationM15">${esc(t('contact.locationM15'))}</option>
            <option value="either" data-i18n="contact.locationEither">${esc(t('contact.locationEither'))}</option>
          </select>
        </div>
        <div class="field field--full">
          <label for="message" data-i18n="contact.message">${esc(t('contact.message'))}</label>
          <textarea id="message" name="message" rows="4" required minlength="8"></textarea>
        </div>
        <button class="btn btn--primary btn--block" type="submit">
          <span data-i18n="contact.submit">${esc(t('contact.submit'))}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" /></svg>
        </button>
        <p class="form-status" id="formStatus" role="status" aria-live="polite"></p>
      </form>
    </div>
  </section>
`;

// ---------- AGENDA (home-page widget) ----------
// Two compact, card-based "posters" — one per studio. Each card
// holds a rolling 7-day pill strip and a list of coalesced slot
// rows for the active day. Read-only teaser: slots link into
// `/agenda` where the actual booking tool lives. See
// `src/lib/publicAgenda.ts` for the runtime that fills the mount.
export const agendaTpl = (): string => `
  <section class="agenda" id="agenda" aria-labelledby="agendaTitle">
    <div class="agenda__inner">
      <header class="section-head reveal">
        <p class="section-eyebrow" data-i18n="agenda.title">${esc(t('agenda.title'))}</p>
        <h2 class="section-title" id="agendaTitle">
          <span data-i18n="agenda.title">${esc(t('agenda.title'))}</span>
        </h2>
        <p class="section-sub" data-i18n="agenda.subtitle">${esc(t('agenda.subtitle'))}</p>
      </header>

      <div class="agenda__legend reveal" aria-label="${esc(t('agendaApp.legend.open'))}">
        <span class="legend legend--open"   data-i18n="agendaApp.legend.open">${esc(t('agendaApp.legend.open'))}</span>
        <span class="legend legend--event"  data-i18n="agendaApp.legend.event">${esc(t('agendaApp.legend.event'))}</span>
        <span class="legend legend--taken"  data-i18n="agendaApp.legend.taken">${esc(t('agendaApp.legend.taken'))}</span>
        <span class="legend legend--closed" data-i18n="agendaApp.legend.closed">${esc(t('agendaApp.legend.closed'))}</span>
      </div>

      <div class="agenda__pane reveal">
        <article class="agenda-card">
          <header class="agenda-card__head">
            <h3 class="agenda-card__title">
              <span class="agenda-card__tag">A22</span>
              <span data-i18n="agenda.studioA22">${esc(t('agenda.studioA22'))}</span>
            </h3>
            <span class="agenda-card__range" id="agendaRangeA22" aria-live="polite"></span>
          </header>
          <div class="agenda-card__body" id="agendaMountA22">
            <p class="agenda__empty" data-i18n="agenda.loading">${esc(t('agenda.loading'))}</p>
          </div>
          <footer class="agenda-card__foot">
            <a class="agenda-card__cta" href="/${getLocale()}/agenda?loc=A22" data-nav data-slug="agenda">
              <span data-i18n="agenda.viewFull">${esc(t('agenda.viewFull'))}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" /></svg>
            </a>
          </footer>
        </article>
        <article class="agenda-card">
          <header class="agenda-card__head">
            <h3 class="agenda-card__title">
              <span class="agenda-card__tag">M15</span>
              <span data-i18n="agenda.studioM15">${esc(t('agenda.studioM15'))}</span>
            </h3>
            <span class="agenda-card__range" id="agendaRangeM15" aria-live="polite"></span>
          </header>
          <div class="agenda-card__body" id="agendaMountM15">
            <p class="agenda__empty" data-i18n="agenda.loading">${esc(t('agenda.loading'))}</p>
          </div>
          <footer class="agenda-card__foot">
            <a class="agenda-card__cta" href="/${getLocale()}/agenda?loc=M15" data-nav data-slug="agenda">
              <span data-i18n="agenda.viewFull">${esc(t('agenda.viewFull'))}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" /></svg>
            </a>
          </footer>
        </article>
      </div>
    </div>
  </section>
`;

// ---------- DYNAMIC PAGE (rendered for admin-created pages) ----------
// When `title` is empty, no <h1> header is rendered (used for the Main page
// where the body already contains its own headings and the navLabel is just a
// label for the admin editor).
export const pageTpl = (pageId: string, title: string, bodyHtml: string): string => `
  <section class="page" id="page-${esc(pageId)}" aria-labelledby="${title ? `page-${esc(pageId)}-title` : ''}">
    <div class="page__inner">
      ${title ? `
      <header class="section-head reveal">
        <h1 class="section-title" id="page-${esc(pageId)}-title">${esc(title)}</h1>
      </header>` : ''}
      <article class="page__body reveal">
        ${bodyHtml}
      </article>
    </div>
  </section>
`;

// Light Markdown → HTML for dynamic pages.
// We deliberately keep this minimal: paragraphs, headings, bold, italic,
// links, lists, code. Everything else passes through escaped.
export function renderMarkdown(src: string): string {
  if (!src) return '';
  const escInline = (s: string) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const lines = src.split(/\r?\n/);
  const out: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inline(para.join(' '))}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (inList) { out.push(`</${inList}>`); inList = null; }
  };

  const inline = (s: string) => {
    let x = escInline(s);
    x = x.replace(/`([^`]+)`/g, '<code>$1</code>');
    x = x.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    x = x.replace(/\b_([^_]+)_\b/g, '<em>$1</em>');
    x = x.replace(/\[([^\]]+)]\(([^)]+)\)/g, (_m, text, href) => {
      // Only allow safe schemes in markdown links.
      if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) return text;
      const safeHref = href.replace(/"/g, '&quot;');
      return `<a href="${safeHref}" rel="noopener noreferrer">${text}</a>`;
    });
    return x;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line) { flushPara(); flushList(); continue; }

    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      flushPara(); flushList();
      out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`);
      continue;
    }
    const ul = /^[-*]\s+(.*)$/.exec(line);
    if (ul) {
      flushPara();
      if (inList !== 'ul') { flushList(); out.push('<ul>'); inList = 'ul'; }
      out.push(`<li>${inline(ul[1])}</li>`);
      continue;
    }
    const ol = /^\d+\.\s+(.*)$/.exec(line);
    if (ol) {
      flushPara();
      if (inList !== 'ol') { flushList(); out.push('<ol>'); inList = 'ol'; }
      out.push(`<li>${inline(ol[1])}</li>`);
      continue;
    }
    para.push(line);
  }
  flushPara(); flushList();
  return out.join('\n');
}